import SectionHeader from "@/components/site/SectionHeader";
import { CheckCircle2, Shield, Wrench, Sparkles, MessageCircle, TrendingUp, Server, Lock } from "lucide-react";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/Reveal";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema, localBusinessSchema } from "@/components/seo/schema";

const REASONS = [
  { icon: TrendingUp, label: "Business-first development" },
  { icon: Wrench, label: "Custom solutions" },
  { icon: Sparkles, label: "Clean modern design" },
  { icon: Server, label: "Hosting & maintenance options" },
  { icon: Lock, label: "Stripe-secure payments" },
  { icon: Shield, label: "Veteran-owned company" },
  { icon: MessageCircle, label: "Clear communication" },
  { icon: CheckCircle2, label: "Built for long-term growth" },
];

export default function About() {
  return (
    <div data-testid="page-about" className="relative">
      <Seo
        title="About Equinox Systems — Veteran-Owned Web Development"
        description="Equinox Systems is a veteran-owned, Freedom Core Ventures company offering the Free Custom Website Program and custom web development, apps, CRMs, and AI to businesses nationwide."
        path="/about"
        schemas={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="glow-orb w-96 h-96 bg-blue-700/15 -top-20 right-0 animate-pulse-glow" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-7">
              <SectionHeader
                as="h1"
                eyebrow="About"
                title="Engineering digital systems that help businesses grow."
              />
              <p className="text-gray-300 leading-relaxed text-lg max-w-2xl">
                Equinox Systems is a web development and digital systems company built to help
                businesses launch stronger websites, automate operations, and deploy custom tools
                that save time and increase revenue. As a Freedom Core Ventures company, we pair
                business strategy with modern engineering to ship practical solutions that scale.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden border border-white/10 aspect-square shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/14589851/pexels-photo-14589851.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Glass office"
                  className="w-full h-full object-cover opacity-90"
                />
              </div>
            </Reveal>
          </div>

          <div className="mt-24">
            <Reveal>
              <SectionHeader
                eyebrow="Why Equinox"
                title="Reasons teams choose us"
              />
            </Reveal>
            <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {REASONS.map((r, i) => (
                <RevealItem
                  key={r.label}
                  className="eq-card rounded-2xl p-6 group"
                  data-testid={`about-reason-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/30 grid place-items-center text-blue-300 transition-all duration-300 group-hover:shadow-[0_0_20px_rgba(10,132,255,0.45)]">
                    <r.icon className="w-5 h-5" strokeWidth={1.7} />
                  </div>
                  <div className="mt-4 font-display text-base font-semibold">{r.label}</div>
                </RevealItem>
              ))}
            </RevealStagger>
          </div>
        </div>
      </section>
    </div>
  );
}
